// src/components/WOEPayment/hooks/useOrderSubmission.js - FIXED VERSION
import { useCallback } from 'react';
import axios from 'axios';
import { isIsraelUser, requiresTaxCalculation , getInvoicePrefix, isTaxExemptOrder, getWarehouseInfo } from '../../../../utils/locationUtils';
import { API_URL } from '../../../../config/api';
import { getCurrentUser } from '../../../../utils/auth';
import { getCurrentTimestamp, toAPIDate } from '../../../../utils/dateUtils';
import israelInvoiceService from '../services/israelInvoiceService';
import { normalizeOrderType } from '../../../../utils/orderTypeUtils';

const useOrderSubmission = ({
    customerInfo,
    wigDetails,
    wigData,
    wigRoomsData,
    orderItems,
    accessories,
    barcode,
    serialCode,
    orderNo,
    paymentCalculations,
    agreementManager,
    appointmentId
}) => {


    const isAccessoryOrder = useCallback(() => {
        return customerInfo?.orderType === 'Accessory';
    }, [customerInfo?.orderType]);

    const determineOrderStatus = useCallback((orderType) => {
        if (orderType === 'Accessory' || orderType === 'Consultation') {
            return 'Completed';
        }
        return 'Active';
    }, []);

    const determineEarliestDueDate = useCallback((roomTasks) => {
        console.log('🔍 Determining due date from room tasks:', roomTasks);
        
        if (!roomTasks || roomTasks.length === 0) {
            console.log('⚠️ No room tasks found, using today as due date');
            return getCurrentTimestamp();
        }
        
        // Helper function to safely create date
        const createSafeDate = (dateString) => {
            if (!dateString) return null;
            
            try {
                // Handle different date formats
                let dateToUse = dateString;
                
                // If it's just YYYY-MM-DD, add time
                if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    dateToUse = dateString + 'T12:00:00';
                }
                // If it's YYYY-MM-DD HH:MM:SS, convert to ISO format
                else if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
                    dateToUse = dateString.replace(' ', 'T');
                }
                // If it already has T but no time, add time
                else if (typeof dateString === 'string' && !dateString.includes('T')) {
                    dateToUse = dateString + 'T12:00:00';
                }
                
                const date = new Date(dateToUse);
                
                if (isNaN(date.getTime())) {
                    console.error('Invalid date created from:', dateString, '-> transformed to:', dateToUse);
                    return null;
                }
                
                return date;
            } catch (error) {
                console.error('Error creating date from:', dateString, error);
                return null;
            }
        };
        
        // 🎯 PRIORITY 1: Look for customer pickup room date
        const pickupRoom = roomTasks.find(task => {
            const roomNum = parseInt(task.roomNo);
            const isPickupRoom = roomNum === 7001 || roomNum === 21001 || roomNum === 32001;
            return isPickupRoom && task.taskDueDate;
        });
        
        if (pickupRoom?.taskDueDate) {
            const pickupDate = createSafeDate(pickupRoom.taskDueDate);
            if (pickupDate) {
                console.log('✅ Using customer pickup date for invoice due date:', pickupDate.toISOString(), 'from room:', pickupRoom.roomNo);
                return pickupDate.toISOString();
            } else {
                console.error('❌ Invalid pickup date:', pickupRoom.taskDueDate);
            }
        }
        
        // 🎯 PRIORITY 2: If no pickup date, use earliest task date as fallback
        const validDatesWithRooms = roomTasks
            .filter(task => task.taskDueDate)
            .map(task => ({
                date: createSafeDate(task.taskDueDate),
                roomNo: task.roomNo,
                roomTask: task.roomTask,
                originalDate: task.taskDueDate
            }))
            .filter(item => item.date !== null) // Remove invalid dates
            .sort((a, b) => a.date - b.date);
        
        if (validDatesWithRooms.length > 0) {
            const earliestTask = validDatesWithRooms[0];
            console.log('✅ Using earliest task date for invoice due date:', earliestTask.date.toISOString(), 'from room:', earliestTask.roomNo);
            return earliestTask.date.toISOString();
        }
        
        // 🎯 PRIORITY 3: Ultimate fallback
        console.log('⚠️ No valid task dates found, using today as due date');
        return getCurrentTimestamp();
    }, []);
    
    const determineWarehouse = useCallback((roomTasks) => {
        if (!roomTasks || roomTasks.length === 0) {
            return 'Pomona';
        }
        
        const locations = new Set(roomTasks.map(task => task.location).filter(Boolean));
        
        if (locations.has('Pomona')) return 'Pomona';
        if (locations.has('Lakewood')) return 'Lakewood';
        if (locations.has('Israel')) return 'Israel';
        if (locations.has('China')) return 'China';
        
        return 'Pomona';
    }, []);

    const prepareRoomTasks = useCallback((roomTasks) => {
   
    if (isAccessoryOrder()) {
        console.log('✅ Accessory order - bypassing room task validation');
        return []; // Return empty array - backend will handle this
    }
     let finalRoomTasks = [...roomTasks];
    // Get location-specific room numbers for validation only
    const warehouseInfo = getWarehouseInfo();
    
    
    // ✅ KEEP: Validate that customer pickup room exists (should be created in UI)
    const pickupRoomExists = finalRoomTasks.some(task => {
        const roomNum = parseInt(task.roomNo);
        return (roomNum === 7001 || roomNum === 21001 || roomNum === 32001) && task.taskDueDate;
    });
    
    if (!pickupRoomExists) {
        // 🚨 VALIDATION ERROR: Pickup room should have been created in WigRooms component
        throw new Error('Customer pickup date is required. Please go back and set a pickup date before submitting the order.');
    }
    
    return finalRoomTasks;
}, [isAccessoryOrder]);

    const needsOrderNumber = useCallback(() => {
        return !orderNo || orderNo.toString().startsWith('TEMP-');
    }, [orderNo]);


    const generateOrderNumber = useCallback(async () => {
        try {
            console.log('🔢 Generating new order number...');
            
            const response = await axios.post(`${API_URL}/api/generate-order-number`, {
                tempOrderNo: orderNo,
                orderType: customerInfo.orderType
            });
            
            if (response.data.success) {
                console.log('✅ Generated order number:', response.data.newOrderNo);
                return response.data.newOrderNo;
            } else {
                throw new Error(response.data.error || 'Failed to generate order number');
            }
        } catch (error) {
            console.error('❌ Error generating order number:', error);
            throw error;
        }
    }, [orderNo, customerInfo.orderType]);
    
    const ensureRealOrderNumber = useCallback(async () => {
        // ✅ FIX: Remove warranty-only restriction
        const hasTempOrderNo = needsOrderNumber();
        
        if (hasTempOrderNo) {
            console.log('🔢 Agreement needs real order number, generating...');
            return await generateOrderNumber();
        }
        
        return orderNo;

    }, [needsOrderNumber, generateOrderNumber, orderNo]);

    
    // Process payments based on order type
    const processPayments = useCallback((payments, orderType) => {
        let processedPayments = [...payments];

        // "Aidel New Wig" follows the same deposit rules as "New Wig".
        if (normalizeOrderType(orderType) === 'New Wig') {
            const totalPaid = payments.reduce((sum, payment) => {
                return sum + (parseFloat(payment.amount) || 0);
            }, 0);

            const totalCardFees = payments.reduce((sum, payment) => {
                return sum + (parseFloat(payment.cardFee) || 0);
            }, 0);
        
            processedPayments = [{
                id: 1,
                method: 'Deposit',
                amount: totalPaid.toFixed(2),
                baseAmount: totalPaid.toFixed(2), // Amount toward balance
                totalCharge: (totalPaid + totalCardFees).toFixed(2), // Total charged to customer
                notes: `Deposit Paid: ${totalPaid.toFixed(2)}`,
                cardFee: totalCardFees, 
                isDepositPayment: true,
                originalPayments: payments
            }];
        } else {
            processedPayments = payments.map(payment => ({
                ...payment,
                cardFee: parseFloat(payment.cardFee || 0),
                baseAmount: parseFloat(payment.amount || 0), // Amount toward balance (excluding fee)
                totalCharge: parseFloat(payment.amount || 0) + parseFloat(payment.cardFee || 0) // Total charged to customer
            }));
        }

        return processedPayments;
    }, []);

    const submitOrderToDatabase = useCallback(async (finalRoomTasks, processedPayments, realOrderNo = null, skipOrderGeneration = false, customerValidation = null, depositBypassReason = null) => {
        // Use provided order number or determine if we need to generate one
        const finalOrderNo = realOrderNo || orderNo;
        const needsGeneration = !skipOrderGeneration && (!realOrderNo && (!orderNo || orderNo.toString().startsWith('TEMP-')));
        
        const orderData = {
            needsOrderNumber: needsGeneration, // Only generate if not already provided
            orderDetails: {
                orderNo: needsGeneration ? null : finalOrderNo, // Use real order number if available
                orderType: customerInfo.orderType,
                orderDate: getCurrentTimestamp(),
                customerName: customerValidation?.customerName || customerInfo.customerName,                
                customerNo: customerValidation?.customerId || customerInfo.customerNo || customerInfo.ID,
                customerAddress: customerInfo.address,
                customerCity: customerInfo.city || '',
                customerState: customerInfo.state || '',
                customerZip: customerInfo.zip || '',
                customerPhone: customerInfo.phone,
                customerEmail: customerInfo.email,
                wigBarCode: barcode || '',
                wigSerialNo: serialCode || '',
                orderNotes: wigDetails?.notes || '',
                wigType: wigDetails?.wigType || '',
                netSize: wigDetails?.netSize || '',
                netType: wigDetails?.netType || '',
                baseColor: wigDetails?.baseColor || '',
                wigLength: wigDetails?.hairLength || '',
                hairType: wigDetails?.hairType || '',
                laceType: wigDetails?.laceType || '',
                cutStyle: wigDetails?.cutStyle || '',
                inventoryType: wigDetails?.inventoryType || '',
                orderStatus: determineOrderStatus(customerInfo.orderType),
                dueDate: determineEarliestDueDate(finalRoomTasks),
                orderWarehouse: determineWarehouse(finalRoomTasks),
                enteredBy: getCurrentUser(),
                userLocation: isIsraelUser() ? 'Israel' : 'US',
                isIsraelOrder: isIsraelUser(),
                processingMode: 'LOCAL',
                appointmentId: appointmentId || null // Link appointment to order
            },
            orderItems: [...orderItems, ...accessories],
            roomTasks: finalRoomTasks,
            paymentInfo: paymentCalculations.payments.map((payment, index) => {
                // ✅ CONSISTENT: Use the exact same rounded values stored in the payment object
                const baseAmount = parseFloat(payment.amount || 0); // Amount toward subtotal
                const taxOnPayment = parseFloat(payment.taxOnPayment || 0); // Already rounded to 2 decimals
                const cardFee = parseFloat(payment.cardFee || 0); // Already rounded to 2 decimals  
                const totalCharge = parseFloat(payment.totalCharge || payment.amount || 0); // Already calculated with rounded components
                
                console.log(`💳 Payment ${index + 1} - EXACT DISPLAY VALUES:`, {
                    method: payment.method,
                    baseAmount: baseAmount,
                    taxOnPayment: taxOnPayment, // Exactly what user sees
                    cardFee: cardFee, // Exactly what user sees
                    totalCharge: totalCharge, // Exactly what user sees
                    verification: Math.abs((baseAmount + taxOnPayment + cardFee) - totalCharge) < 0.01 ? '✅' : '❌',
                    note: 'Backend receives IDENTICAL values to what user sees on screen'
                });
                
                // Append bypass reason to first payment's notes so it's persisted in PaymentNotes
                const bypassPrefix = index === 0 && depositBypassReason
                    ? `DEPOSIT BYPASS: ${depositBypassReason}${payment.notes ? ' | ' : ''}`
                    : '';

                return {
                    // Core payment data
                    method: payment.method,
                    notes: bypassPrefix + (payment.notes || ''),

                    // Card on File tracking
                    cloverTransactionId: payment.cloverTransactionId || null,
                    isCardOnFile: payment.isCardOnFile || false,

                    // ✅ CONSISTENT: Send rounded values that match display exactly
                    amount: baseAmount, // Backend PaymentAmount field
                    baseAmount: baseAmount, // Amount toward order (excluding tax/fees)
                    customerPaymentAmount: totalCharge, // EXACT total shown to customer
                    
                    // ✅ CONSISTENT: Tax allocation - exact display values
                    taxFromPayment: taxOnPayment, // EXACT tax amount shown to customer
                    taxOnPayment: taxOnPayment, // Backwards compatibility
                    
                    // ✅ CONSISTENT: Fee handling - exact display values  
                    cardFee: cardFee, // EXACT processing fee shown to customer
                    processingFee: cardFee, // Alternative field name
                    
                    // ✅ CONSISTENT: Total verification - exact display value
                    totalCharge: totalCharge, // EXACT total charge shown to customer
                    
                    // Enhanced metadata for backend logging
                    paymentBreakdown: {
                        towardOrder: baseAmount,
                        taxPortion: taxOnPayment,
                        processingFee: cardFee,
                        customerTotal: totalCharge,
                        calculationVerified: Math.abs((baseAmount + taxOnPayment + cardFee) - totalCharge) < 0.01,
                        
                        // ✅ CONSISTENCY: Confirm values match display
                        consistencyCheck: {
                            taxDisplayValue: taxOnPayment,
                            cardFeeDisplayValue: cardFee,
                            totalChargeDisplayValue: totalCharge,
                            note: 'These values EXACTLY match what customer sees on screen'
                        }
                    },
                    
                    // Order context
                    orderType: customerInfo.orderType,
                    paymentDate: getCurrentTimestamp(),
                    isDepositPayment: normalizeOrderType(customerInfo.orderType) === 'New Wig',
                    paymentSequence: index + 1
                };
            }),
            totals: {
                subtotal: parseFloat(paymentCalculations.totals.subtotal),
                tax: parseFloat(paymentCalculations.totals.tax),
                taxRate: parseFloat(paymentCalculations.totals.taxRate),
                total: parseFloat(paymentCalculations.totals.total),
                cardFees: parseFloat(paymentCalculations.paymentSummary.totalCardFees || 0)
            }
        };

        console.log('🔍 Frontend - Submitting order with appointmentId:', orderData.orderDetails.appointmentId);

        const response = await axios.post(`${API_URL}/api/submit-order`, orderData);
        console.log('✅ Order submitted successfully. Real order number:', response.data.orderNo);
        
        return response.data;
    }, [
        orderNo, customerInfo, barcode, serialCode, wigDetails, orderItems, accessories,
        paymentCalculations, determineOrderStatus, determineEarliestDueDate,
        determineWarehouse, getCurrentUser, appointmentId
    ]);

    // Update inventory status
    const updateInventoryStatus = useCallback(async (realOrderNo, depositBypassActive = false) => {
        try {
            const customerId = customerInfo.customerNo || customerInfo.ID;
            console.log('🔄 Updating inventory status for order:', realOrderNo, 'Customer:', customerId);
            await axios.post(`${API_URL}/api/update-inventory-status`, {
                orderNo: realOrderNo,
                orderItems: [...orderItems, ...accessories],
                orderType: customerInfo.orderType,
                totalPayments: parseFloat(paymentCalculations.paymentSummary.totalPaid),
                customerNo: customerId,
                depositBypassActive: depositBypassActive
            });
            console.log('✅ Inventory status updated successfully');
            return { success: true };
        } catch (inventoryError) {
            console.error('⚠️ Inventory update failed (order still successful):', inventoryError);
            return { success: false, error: inventoryError.message };
        }
    }, [orderItems, accessories, customerInfo.orderType, customerInfo.customerNo, customerInfo.ID, paymentCalculations.paymentSummary.totalPaid]);

    const barcodeToUse = barcode || wigData?.barcode || wigDetails?.barcode || '';
    const serialToUse = serialCode || wigData?.serialCode || wigDetails?.serialCode || '';

    // 🔧 FIXED: Handle invoice creation (Israel vs QuickBooks) with proper tax and fee handling
    const createInvoice = useCallback(async (orderData) => {
        const realOrderNo = orderData.orderNo;

        if (isAccessoryOrder()) {
                console.log('💰 Accessory order - simplified invoice processing');
                // Accessory orders are typically simpler, but still follow same invoice logic
            }
        
        if (isIsraelUser()) {
            console.log('🇮🇱 Processing Israel order...');

            const invoiceResult = await israelInvoiceService.createInvoice({
                orderNo: realOrderNo,
                customerInfo,
                orderItems,
                accessories,
                totals: paymentCalculations.totals,
                payments: paymentCalculations.payments,
                paymentSummary: paymentCalculations.paymentSummary,
                agreementData: agreementManager?.agreementState?.signedData || null,
                getCurrentUser
            });

            if (invoiceResult.success) {
                console.log('✅ Local Israel invoice created:', invoiceResult.invoiceNumber);

                // Save local invoice info to database
                try {
                    await axios.put(`${API_URL}/api/orders/${realOrderNo}/israel-invoice`, {
                        invoiceNumber: invoiceResult.invoiceNumber,
                        invoiceData: invoiceResult.invoiceData,
                        processingMode: 'LOCAL'
                    });
                    console.log('✅ Local invoice info saved to database');
                } catch (dbError) {
                    console.error('⚠️ Failed to save local invoice to database:', dbError);
                }
            }

            return invoiceResult;
        } else {
            console.log('🇺🇸 Processing US order - local invoice...');
            return {
                success: true,
                invoiceNumber: `US-${realOrderNo}`,
                processingMode: 'LOCAL'
            };
        }
    }, [
        customerInfo, orderItems, accessories, paymentCalculations, agreementManager,
        determineEarliestDueDate, wigRoomsData, getCurrentUser, isAccessoryOrder
    ]);
   

    // Main submission function
    const submitOrder = useCallback(async (options = {}) => {
        // Support legacy positional boolean arg as well as new options object
        const needsOrderGeneration = typeof options === 'boolean' ? options : (options.needsOrderGeneration ?? null);
        const depositBypassReason = typeof options === 'object' && options !== null ? (options.depositBypassReason || null) : null;

        try {
            console.log('🚀 Starting order submission process...');
            const customerValidation = (() => {
                const customerId = customerInfo.customerNo || customerInfo.ID;
                const customerName = customerInfo.customerName;
                
                if (!customerId) {
                    throw new Error('Customer ID is required but missing. Please ensure customer is properly selected.');
                }
                
                if (!customerName) {
                    throw new Error('Customer name is required but missing. Please verify customer information.');
                }
                
                console.log('✅ Customer validation passed:', {
                    customerId,
                    customerName,
                    orderType: customerInfo.orderType
                });
                
                return { customerId, customerName };
            })();
            // Step 1: Prepare room tasks
            const finalRoomTasks = prepareRoomTasks(wigRoomsData);
            
            // Step 2: Process payments
            const processedPayments = processPayments(paymentCalculations.payments, customerInfo.orderType);
            
            // Step 3: Determine if we need to generate an order number
            let realOrderNo = orderNo;
            let orderWasGenerated = false;
            
            // Check if order number generation is needed
            const shouldGenerateOrderNo = needsOrderGeneration !== null ? 
                needsOrderGeneration : 
                (!orderNo || orderNo.toString().startsWith('TEMP-'));
            
            if (shouldGenerateOrderNo) {
                try {
                    console.log('🔢 Generating order number for submission...');
                    
                    // Use the generateOrderNumber function
                    realOrderNo = await generateOrderNumber();
                    orderWasGenerated = true;
                    console.log('✅ Generated order number for submission:', realOrderNo);
                } catch (orderError) {
                    console.error('❌ Failed to generate order number:', orderError);
                    throw new Error(`Order number generation failed: ${orderError.message}`);
                }
            } else {
                console.log('✅ Using existing order number:', realOrderNo);
            }

            // Step 5: Submit order to database with real order number
            const orderData = await submitOrderToDatabase(finalRoomTasks, processedPayments, realOrderNo, !orderWasGenerated, customerValidation, depositBypassReason);
            const finalOrderNo = orderData.orderNo;

      
            // Step 6: Update inventory
            await updateInventoryStatus(finalOrderNo, !!depositBypassReason);

            // Step 6.5: Link Send Out appointment to real order number (if appointment was created before order existed)
            if (appointmentId) {
                try {
                    await axios.patch(`${API_URL}/api/appointments/${appointmentId}/link-order`, { orderNo: finalOrderNo });
                    console.log('✅ Appointment linked to order:', finalOrderNo);
                } catch (linkError) {
                    console.error('⚠️ Failed to link appointment to order (non-fatal):', linkError.message);
                }
            }

            // Step 7: Create invoice (Israel vs QuickBooks)
            console.log('🔍 Step 7 - Invoice handling.');
            const invoiceResult = await createInvoice(orderData);
            
            // Step 8: Determine final invoice number
            const finalInvoiceNumber = invoiceResult?.invoiceNumber || 
                                        invoiceResult?.qbInvoiceNumber || 
                                        `${getInvoicePrefix()}${finalOrderNo}`;
            
            // Step 9: Prepare success result
            const result = {
                success: true,
                orderNo: finalOrderNo,
                invoiceNumber: finalInvoiceNumber,
                invoiceResult,
                isIsraelOrder: isIsraelUser(),
                orderWasGenerated, // Track if order number was generated during submission
                
                // Summary updates for parent component
                summaryUpdates: {
                    invoiceNumber: finalInvoiceNumber,
                    isIsraelInvoice: isIsraelUser(),
                    israelInvoiceCreated: invoiceResult?.success && isIsraelUser(),
                    invoiceMessage: invoiceResult?.message,
                    processingMode: 'LOCAL'
                },
                
                // Navigation state for work order profile
                navigationState: {
                    orderDetails: {
                        ...orderData,
                        wigBarCode: barcode,
                        wigSerialNo: serialCode
                    },
                    customerInfo,
                    wigDetails: wigData || wigDetails,
                    orderItems,
                    accessories,
                    payments: paymentCalculations.payments.map(payment => ({
                        ...payment,
                        amount: payment.baseAmountTowardSubtotal || payment.amount, // FIX: Send base amount (subtotal), not total
                        baseAmount: payment.baseAmountTowardSubtotal || payment.amount, // Ensure baseAmount is set
                        originalAmount: payment.amount,
                        totalCharge: payment.totalCharge || payment.amount,
                        cardFee: payment.cardFee || 0,
                        hasTaxAllocation: (payment.taxOnPayment && parseFloat(payment.taxOnPayment) > 0) ? true : false,
                        taxOnPayment: payment.taxOnPayment || 0
                    })),
                    roomTasks: finalRoomTasks,
                    totals: paymentCalculations.totals,
                    paymentSummary: {
                        ...paymentCalculations.paymentSummary,
                        invoiceNumber: finalInvoiceNumber,
                        isIsraelInvoice: isIsraelUser(),
                        invoiceCreated: invoiceResult?.success || false,
                        processingMode: 'LOCAL'
                    },
                    invoiceResult,
                    isIsraelOrder: isIsraelUser()
                }
            };
    
            return result;
            
        } catch (error) {
            console.error('❌ Order submission failed:', error);
            
            let errorMessage = `Order submission failed: ${error.message}`;
            
            if (error.response?.data?.stage) {
                errorMessage += ` (Failed at: ${error.response.data.stage})`;
            }
            
            if (error.code === 'ERR_NETWORK') {
                errorMessage = 'Network error: Please check your connection.';
            }
            
            return {
                success: false,
                error: errorMessage,
                details: error.response?.data
            };
        }
        }, [
        wigRoomsData, paymentCalculations, customerInfo, prepareRoomTasks, processPayments,
        submitOrderToDatabase, updateInventoryStatus, createInvoice, barcode, serialCode,
        wigData, wigDetails, orderItems, accessories, orderNo, generateOrderNumber
        ]);
    

    return {
    submitOrder,
    generateOrderNumber,
    ensureRealOrderNumber,
    needsOrderNumber,
    getCurrentUser,
    
    // Helper functions
    prepareRoomTasks,
    processPayments,
    createInvoice,
    
    // Status helpers
    isIsraelOrder: isIsraelUser(),
    isAccessoryOrder: isAccessoryOrder(), // Add this
    processingMode: 'LOCAL'
};
};

export default useOrderSubmission;